import { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import SearchInput from "../../../utils/SearchInput";
import TableLoader from "../../../utils/TableLoader";
import PaginationComponent from "../../../utils/PaginationComponent";
import { FaPlus } from "react-icons/fa";
import { BiSolidTrashAlt } from "react-icons/bi";
import { TbEdit } from "react-icons/tb";
import { motion, AnimatePresence } from "framer-motion";
import { buttonAnimations, cardVariants, containerVariants, dialogVariants, itemVariants } from "../../../utils/Animations";
import AllergensAdd from "./AllergensAdd";
import AllergensEdit from "./AllergensEdit";
import { ReactSortable } from "react-sortablejs";

const AllergensManagement = ((props, ref) => {
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");
    const [isLoading, setIsLoading] = useState({ main: false, edit: false });
    const [allergensData, setAllergensData] = useState({});
    const [allergensListData, setAllergensListData] = useState([]);
    const [currentAllergensData, setCurrentAllergensData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isAdd, setIsAdd] = useState(false);
    const [dialog, setDialog] = useState(false);
    const [selectedAllergenId, setSelectedAllergenId] = useState(null);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const [searchText, setSearchText] = useState("");
    const handleSearchChange = (value) => {
        setSearchText(value);
    };

    const getAllergensData = useCallback(async () => {
        setIsLoading(prev => ({ ...prev, main: true }));
        try {
            const params = {
                pageNumber: page,
                size: rowsPerPage,
                ...(searchText && { search: searchText }),
            };

            const response = await axios.get(`${apiUrl}/api/v1/allergens`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    params
                }
            );
            const responseData = response?.data;
            setAllergensListData(responseData?.data || []);
            setTotalPages(responseData?.total || 0);
        } catch (error) {
            console.error('Error fetching allergens data:', error);
            toast.error(
                error.response?.data?.message || "Failed to fetch allergens",
                {
                    theme: "colored",
                    autoClose: 3000,
                    hideProgressBar: true
                }
            );
        } finally {
            setIsLoading(prev => ({ ...prev, main: false }));
        }
    }, [apiUrl, token, page, rowsPerPage, searchText]);

    // const handleSortEnd = (newList) => {
    //     const updatedItems = newList.map((item, index) => ({
    //         ...item,
    //         sequence: index + 1,
    //     }));
    //     setAllergensListData(updatedItems);
    //     updateAllergensSequence(updatedItems);
    // };

    const handleSortEnd = (newList) => {
        // Check if the order actually changed
        const orderChanged = newList.some((item, index) =>
            item.id !== allergensListData[index]?.id
        );
 
        if (orderChanged) {
            const updatedItems = newList.map((item, index) => ({
                ...item,
                sequence: index + 1,
            }));
 
            setAllergensListData(updatedItems);
            updateAllergensSequence(updatedItems);
        }
    };

    const updateAllergensSequence = async (items, retryCount = 0) => {
        try {
            if (!token) throw new Error("No token found");

            // Skip if items are empty (no error thrown to client)
            if (!items || !Array.isArray(items) || items.length === 0) {
                console.warn("Allergens data is empty. Skipping update.");
                return;
            }

            const response = await axios.post(
                `${apiUrl}/api/v1/allergens-update-sequence`,
                {
                    allergens: items.map((item) => ({
                        id: item.id,
                        sequence: item.sequence,
                    })),
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response?.data?.message || "Order updated successfully!", {
                theme: "colored",
                autoClose: 1000,
            });
        } catch (error) {
            console.error("Error updating sequence:", error);

            // Retry logic (e.g., max 3 retries with 1-second delay)
            if (retryCount < 3) {
                await new Promise(resolve => setTimeout(resolve, 1000)); // 1-second delay
                return updateAllergensSequence(items, retryCount + 1); // Retry
            }

            // Only show error to client if not a blank data issue
            if (!error.message.includes("No allergens data provided")) {
                toast.error(error?.response?.data?.message || "Update failed", {
                    theme: "colored",
                    autoClose: 3000,
                });
            }
        }
    };

    const handleSave = async () => {
        setIsLoading({ ...isLoading, edit: true });
        try {
            if (!token) {
                console.error('No token found in localStorage.');
                return;
            }
            const formData = new FormData();
            formData.append("name", allergensData.name);
            // formData.append("name_it", allergensData.name_it);
            // formData.append("sequence", allergensData.sequence);
            const response = await axios.post(
                `${apiUrl}/api/v1/allergens/${currentAllergensData?.id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            getAllergensData();
            toast.success(response?.data?.message || "Allergens update successfully!", {
                theme: "colored",
                autoClose: 1000,
            });
            setIsEditing(false);
            navigate(`/admin/allergensManagement`);
        } catch (error) {
            console.error("Error adding allergens:", error?.response?.data?.errors);
            const errors = error?.response?.data?.errors;
            if (errors) {
                for (const key in errors) {
                    if (Array.isArray(errors[key])) {
                        errors[key].forEach((message) => {
                            toast.error(message, {
                                theme: "colored",
                                autoClose: 3000,
                            });
                        });
                    }
                }
            } else {
                toast.error(error?.response?.data?.message, {
                    theme: "colored",
                    autoClose: 3000,
                });
            }
        } finally {
            setIsLoading({ ...isLoading, edit: false });
        }
    };

    const handleDelete = async (id) => {
        if (!id) {
            console.error('Error: ID is required to delete a allergens.');
            return;
        }
        setIsLoading({ ...isLoading, main: true });
        try {
            const response = await axios.delete(`${apiUrl}/api/v1/allergens/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            // Then update the sequence of the remaining courses
            const updatedCourses = allergensListData
                .filter(allergens => allergens.id !== id)
                .map((allergens, index) => ({
                    ...allergens,
                    sequence: index + 1
                }));

            await updateAllergensSequence(updatedCourses);
            await getAllergensData();
            setTimeout(() => {
                toast.success(response?.data?.message || "Allergens deleted successfully!", {
                    theme: "colored",
                    autoClose: 1000,
                });
            }, 1500);
        } catch (error) {
            console.error('Error deleting Allergens:', error?.response?.data?.message || error.message);
            toast.error("Failed to delete the Allergens. Please try again.", {
                theme: "colored",
                autoClose: 3000,
            });
        } finally {
            setIsLoading({ ...isLoading, main: false });
        }
    };

    const handleEditClick = (user) => {
        setIsEditing(true);
        setCurrentAllergensData(user);
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const handleCancel = () => {
        setIsEditing(false);
        setCurrentAllergensData(null);
    };

    useImperativeHandle(ref, () => ({
        allergens() {
            setIsEditing(false);
            setIsAdd(false);
            setCurrentAllergensData(null);
        },
    }));

    useEffect(() => {
        if (currentAllergensData) {
            setAllergensData({
                name: currentAllergensData?.name || "",
                sequence: currentAllergensData?.sequence || "",
            });
        }
    }, [currentAllergensData]);

    useEffect(() => {
        getAllergensData();
    }, [page, rowsPerPage, searchText, getAllergensData]);

    useEffect(() => {
        const handlePageRefresh = () => {
            localStorage.removeItem("searchTextUser");
        };
        window.addEventListener("beforeunload", handlePageRefresh);
        return () => {
            window.removeEventListener("beforeunload", handlePageRefresh);
        };
    }, []);

    return (
        <>
            <AnimatePresence mode="wait">
                {isAdd ? (
                    <motion.div
                        key="add-course"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <AllergensAdd
                            setIsAdd={setIsAdd}
                            getAllergensData={getAllergensData}
                        />
                    </motion.div>
                ) : isEditing ? (
                    <motion.div
                        key="edit-course"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <AllergensEdit
                            allergensData={allergensData}
                            setAllergensData={setAllergensData}
                            isLoading={isLoading}
                            handleSave={handleSave}
                            handleCancel={handleCancel}
                        />
                    </motion.div>
                ) : (
                    <motion.section
                        key="course-list"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="border-t border-[#D9D9D9] sm:mt-5 sm:pt-10 pt-5"
                    >
                        <motion.div
                            className="flex lg:flex-row flex-col justify-between items-center mt-3 mb-3 w-full"
                            variants={containerVariants}
                        >
                            <motion.h3 className="text-3xl font-bold mb-2 poppins-medium self-start" variants={itemVariants}>
                                Gestisci Allergeni
                            </motion.h3>
                            <motion.div
                                className="self-start flex items-center gap-4 sm:flex-row flex-col sm:w-auto w-full"
                                variants={itemVariants}
                            >
                                <SearchInput
                                    onSearchChange={handleSearchChange}
                                    component="Allergens Management"
                                    placeholder="Allergens"
                                />
                                <motion.button
                                    className="flex sm:w-auto w-full items-center justify-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] shadow-sm hover:bg-[#8A2533] transition-all duration-200 group"
                                    onClick={() => setIsAdd(true)}
                                    aria-label="Aggiungi Nuovo Allergens"
                                    {...buttonAnimations}
                                >
                                    <FaPlus />
                                    <span className="ml-2 poppins-regular">
                                        Aggiungi Allergeni
                                    </span>
                                </motion.button>
                            </motion.div>
                        </motion.div>
                        <motion.div
                            className="user-mangement-main w-auto h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow"
                            variants={cardVariants}
                        >
                            <table className="w-full text-left wine_table">
                                <thead>
                                    <motion.tr
                                        className="grid grid-cols-[35%_30%_30%] items-center border-b border-[#8B2433]/12 py-2 px-2"
                                        variants={itemVariants}
                                    >
                                        <th className="pb-3 px-2 text-left poppins-medium text-[#6B6B6B]">Nome</th>
                                        <th className="pb-3 px-2 text-center poppins-medium text-[#6B6B6B]">Posizione</th>
                                        <th className="pb-3 px-2 text-right poppins-medium text-[#6B6B6B]">Opzioni</th>
                                    </motion.tr>
                                </thead>
                                {isLoading.main ? (
                                    <TableLoader />
                                ) : (
                                    <tbody>
                                        <ReactSortable
                                            list={allergensListData}
                                            setList={handleSortEnd}
                                            handle=".drag-handle"
                                            animation={150}
                                        >
                                            {allergensListData && allergensListData?.length > 0 ? (
                                                allergensListData?.map((allergen, index) => (
                                                    <tr
                                                        key={index}
                                                        className="grid grid-cols-[35%_30%_30%] items-center border-b border-[#8B2433]/12 py-4 px-2"
                                                    >
                                                        {/* Name with Drag Handle - Left aligned */}
                                                        <td>
                                                            <p className="flex items-center space-x-4 px-2">
                                                                <span className="drag-handle cursor-move">☰</span>
                                                                <span className="poppins-medium text-[#303030]">
                                                                    {allergen?.name?.replace(/\s+/g, " ") || "N/A"}
                                                                </span>
                                                            </p>
                                                        </td>

                                                        {/* Sequence Number - Center aligned */}
                                                        <td className="px-2">
                                                            <div className="w-full flex justify-center">
                                                                <span className="poppins-medium text-[#303030]">
                                                                    {allergen?.sequence || "-"}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Action Buttons - Right aligned */}
                                                        <td className="flex items-center justify-end space-x-2 px-2">
                                                            <motion.button
                                                                className="text-[#8B2433]"
                                                                onClick={() => handleEditClick(allergen)}
                                                                {...buttonAnimations}
                                                            >
                                                                <TbEdit size="20px" />
                                                            </motion.button>
                                                            <motion.button
                                                                className="text-red-700"
                                                                onClick={() => {
                                                                    setDialog(true);
                                                                    setSelectedAllergenId(allergen?.id);
                                                                }}
                                                                {...buttonAnimations}
                                                            >
                                                                <BiSolidTrashAlt size="18px" />
                                                            </motion.button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <motion.tr variants={itemVariants}>
                                                    <td colSpan={3} className="text-center py-4">
                                                        No allergens found
                                                    </td>
                                                </motion.tr>
                                            )}
                                        </ReactSortable>
                                    </tbody>
                                )}
                            </table>
                        </motion.div>
                        {/* {allergensListData && allergensListData?.length > 0 && (
                            <PaginationComponent
                                page={page}
                                totalPages={totalPages}
                                rowsPerPage={rowsPerPage}
                                setPage={setPage}
                                setRowsPerPage={setRowsPerPage}
                            />
                        )} */}
                    </motion.section>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal rendered via portal to center in viewport */}
            {typeof document !== "undefined" &&
                createPortal(
                    <AnimatePresence>
                        {dialog && (
                            <motion.div
                                className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                                variants={dialogVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <motion.div
                                    className="bg-white rounded-lg shadow-lg sm:w-1/3 w-[280px]"
                                    variants={dialogVariants}
                                >
                                    <div className="px-6 py-4 border-b">
                                        <h3 className="text-lg font-semibold text-gray-800 poppins-medium">
                                            Are you sure you want to delete the Allergen?
                                        </h3>
                                    </div>
                                    <div className="px-6 py-4 flex justify-end space-x-4">
                                        <motion.button
                                            onClick={() => {
                                                handleDelete(selectedAllergenId);
                                                setDialog(false);
                                                setSelectedAllergenId(null);
                                            }}
                                            className="px-4 py-2 poppins-regular bg-[#8A2533] text-white rounded-md hover:bg-[#f6efef] hover:text-[#8A2533] transition-colors"
                                            {...buttonAnimations}
                                        >
                                            Yes
                                        </motion.button>
                                        <motion.button
                                            onClick={() => {
                                                setDialog(false);
                                                setSelectedAllergenId(null);
                                            }}
                                            className="px-4 py-2 poppins-regular bg-gray-300 text-gray-700 rounded-md hover:text-white hover:bg-[#8A2533] transition-colors"
                                            {...buttonAnimations}
                                        >
                                            No
                                        </motion.button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>,
                    document.body
                )}
        </>
    );
});

export default forwardRef(AllergensManagement);
